import {lazyWithReload} from './lazyWithReload'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import {enumPolicies} from '../../_metronic/helpers'
import PolicyChecker from '../../_metronic/helpers/components/PolicyChecker'
import ProductDetailRedirect from '../pages/catalog-management/product/ProductDetailRedirect'
import LegacyOrderRedirect from './LegacyOrderRedirect'
import {LazyOutlet} from './SuspensedView'

// Every page is code-split (ADR-0022): a route's chunk is fetched the first
// time it is visited, behind one Suspense boundary inside the layout shell.
// Redirects (`Navigate`, `ProductDetailRedirect`, `LegacyOrderRedirect`) stay eager.
const DashboardWrapper = lazyWithReload(() => import('../pages/dashboard/DashboardWrapper').then((m) => ({default: m.DashboardWrapper})))
const BuilderPageWrapper = lazyWithReload(() => import('../pages/layout-builder/BuilderPageWrapper'))
const UserPage = lazyWithReload(() => import('../pages/user-management/UserPage'))
const UserEditPage = lazyWithReload(() => import('../pages/user-management/user'))
const CustomerPage = lazyWithReload(() => import('../pages/customer-management/customer'))
const ProfilePage = lazyWithReload(() => import('../pages/profile/profile'))
const ProductsPage = lazyWithReload(() => import('../pages/catalog-management/product/products'))
const ProductPage = lazyWithReload(() => import('../pages/catalog-management/product/product'))
const CategoryPage = lazyWithReload(() => import('../pages/catalog-management/category/CategoryPage'))
const SupplierPage = lazyWithReload(() => import('../pages/user-management/supplier/supplier'))
const StocksPage = lazyWithReload(() => import('../pages/stock-management/stock/stocks'))
const StockPage = lazyWithReload(() => import('../pages/stock-management/stock/stock'))
const StockOperation = lazyWithReload(() => import('../pages/stock-management/stock/stock-operation'))
const DevSupportTicketsPage = lazyWithReload(() => import('../pages/dev-support/devSupportTickets'))
const PrintPage = lazyWithReload(() => import('../pages/stock-management/stock/print'))
const PricePage = lazyWithReload(() => import('../pages/price-management/PricePage'))
const VerandaCalculator = lazyWithReload(() => import('../pages/veranda-calculator/verandaCalculator'))
const Checkout = lazyWithReload(() => import('../pages/checkout/checkout'))
const CheckoutCart = lazyWithReload(() => import('../pages/checkout/cart'))
const CheckoutCutList = lazyWithReload(() => import('../pages/checkout/cut-list'))
const CheckoutDetails = lazyWithReload(() => import('../pages/checkout/details'))
const CheckoutThanks = lazyWithReload(() => import('../pages/checkout/checkout-thanks'))
const Orders = lazyWithReload(() => import('../pages/order/orders'))
const Order = lazyWithReload(() => import('../pages/order/order'))
const Pay = lazyWithReload(() => import('../pages/checkout/pay'))
const Contents = lazyWithReload(() => import('../pages/localization/contents'))
const Resources = lazyWithReload(() => import('../pages/localization/resources'))
const Leads = lazyWithReload(() => import('../pages/lead-management/leads'))
const Invoices = lazyWithReload(() => import('../pages/invoice/invoices'))
const Invoice = lazyWithReload(() => import('../pages/invoice/invoice'))
const Quotes = lazyWithReload(() => import('../pages/quote/quotes'))
const Quote = lazyWithReload(() => import('../pages/quote/quote'))
const CompanySetting = lazyWithReload(() => import('../pages/setting/companySetting'))
const InvoiceSetting = lazyWithReload(() => import('../pages/setting/invoiceSetting'))
const QuotationSetting = lazyWithReload(() => import('../pages/setting/quotationSetting'))
const DocumentTextSetting = lazyWithReload(() => import('../pages/setting/documentTextSetting'))
const TenantResetSetting = lazyWithReload(() => import('../pages/setting/tenantResetSetting'))
const EmailSettings = lazyWithReload(() => import('../pages/setting/emailSettings'))
const NotificationTemplateSettings = lazyWithReload(() => import('../pages/setting/notificationTemplateSettings'))
const NotificationSubscriptionSettings = lazyWithReload(() => import('../pages/setting/notificationSubscriptionSettings'))
const Discounts = lazyWithReload(() => import('../pages/discount-management/discounts'))
const Discount = lazyWithReload(() => import('../pages/discount-management/discount'))
const Productions = lazyWithReload(() => import('../pages/production/productions'))
const NotificationsPage = lazyWithReload(() => import('../pages/notifications/notificationsPage'))

const PrivateRoutes = () => {

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        <Route element={<LazyOutlet />}>
          {/* Pages */}
          <Route path='dashboard' element={<DashboardWrapper />} />
          <Route path='builder' element={<BuilderPageWrapper />} />

          {/* Lazy Modules */}

          <Route
            path='user-management'
            element={
              <PolicyChecker
                policies={[
                  enumPolicies.UserWrite,
                  enumPolicies.UserEdit,
                  enumPolicies.UserDealerRead,
                  enumPolicies.CustomerRead,
                  enumPolicies.CustomerEdit,
                  enumPolicies.CustomerWrite,
                  enumPolicies.ProductRead,
                  enumPolicies.ProductEdit,
                  enumPolicies.ProductWrite,
                  enumPolicies.ProductPriceRead,
                ]}
              >
                <UserPage />
              </PolicyChecker>
            }
          />
          {/* The four lists live on one tabbed page (?tab=); the old standalone
              list URLs keep working as deep links into their tab. */}
          <Route path='user-management/users' element={<Navigate to='/user-management?tab=users' replace />} />
          <Route path='user-management/users/:id' element={<UserEditPage />} />
          <Route path='customer-management/customers' element={<Navigate to='/user-management?tab=customers' replace />} />
          <Route path='customer-management/customers/:id' element={<CustomerPage />} />

          <Route path='catalog-management/products' element={<ProductsPage />} />
          <Route path='catalog-management/products/:id' element={<ProductPage />} />
          <Route path='catalog-management/products/:id/detail' element={<ProductDetailRedirect />} />
          <Route path='catalog-management/categories' element={<CategoryPage />} />

          <Route path='user-management/suppliers' element={<Navigate to='/user-management?tab=suppliers' replace />} />
          <Route path='user-management/suppliers/:id' element={<SupplierPage />} />

          {/* Links older in-app notifications still carry (ADR-0036). */}
          <Route path='stock-management' element={<Navigate to='/stock-management/stocks' replace />} />
          <Route path='stock-management/stocks' element={<StocksPage />} />
          <Route path='stock-management/stocks/:id' element={<StockPage />} />

          <Route path='price-management' element={<PricePage />} />
          <Route path='veranda-calculator' element={<VerandaCalculator />} />

          <Route path='checkout-management/checkout' element={<Checkout />} />
          {/* The checkout steps (ADR-0040); the old summary URL lands on the cart. */}
          <Route path='checkout-management/cart' element={<CheckoutCart />} />
          <Route path='checkout-management/cut-list' element={<CheckoutCutList />} />
          <Route path='checkout-management/details' element={<CheckoutDetails />} />
          <Route path='checkout-management/checkout-summary' element={<Navigate to='/checkout-management/cart' replace />} />
          <Route path='checkout-management/pay' element={<Pay />} />
          <Route path='checkout-management/checkout-thanks/:id' element={<CheckoutThanks />} />

          <Route path='order-management/orders' element={<Orders />} />
          <Route path='order-management/orders/:id' element={<Order />} />
          <Route path='orders/:id' element={<LegacyOrderRedirect />} />

          <Route path='order-management/invoices' element={<Invoices />} />
          <Route path='order-management/invoices/:id' element={<Invoice />} />

          <Route path='order-management/quotes' element={<Quotes />} />
          <Route path='order-management/quotes/:id' element={<Quote />} />

          <Route path='order-management/discounts' element={<Discounts />} />
          <Route path='order-management/discounts/:id' element={<Discount />} />

          <Route path='production-management/productions' element={<Productions />} />

          <Route path="stock-management/stocks-operation" element={<StockOperation />}>
            <Route path=":id" element={<StockOperation />} />
          </Route>

          <Route path="stock-management/stocks-print" element={<PrintPage />}>
            <Route path=":id" element={<PrintPage />} />
          </Route>

          <Route path='profile' element={<ProfilePage />} />
          <Route path='notifications' element={<NotificationsPage />} />

          {/* Help */}
          <Route path='dev-support/tickets' element={<DevSupportTicketsPage />} />
          <Route path='dev-support/tickets/:id' element={<DevSupportTicketsPage />} />

          <Route path='localization/contents' element={<Contents />} />
          <Route path='localization/resources' element={<Resources />} />

          <Route path='lead-management/leads' element={<Leads />} />

          <Route path='setting-management/company' element={<CompanySetting />} />
          <Route path='setting-management/invoice' element={<InvoiceSetting />} />
          <Route path='setting-management/quotation' element={<QuotationSetting />} />
          <Route path='setting-management/document-texts' element={<DocumentTextSetting />} />
          <Route path='setting-management/tenant-reset' element={<TenantResetSetting />} />
          <Route path='setting-management/email' element={<EmailSettings />} />
          <Route path='setting-management/notification-template' element={<NotificationTemplateSettings />} />
          <Route path='setting-management/notification-subscriptions' element={<NotificationSubscriptionSettings />} />

        </Route>
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

export { PrivateRoutes }
